import { RiLoader4Line } from "@remixicon/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

interface PriceReport {
	id: string;
	filename: string;
	reportPeriodStart?: string | null;
	reportPeriodEnd?: string | null;
	successCount: number;
	conflictCount: number;
	errorCount: number;
	lineCount: number;
	ingestedAt: string;
}

interface PriceResultLine {
	sku?: string | null;
	price?: number | null;
	priceType?: string | null;
	transactionType?: string | null;
	resultType: string;
	resultCode?: string | null;
	resultDesc?: string | null;
}

interface PriceReportDetail {
	report: PriceReport;
	lines: PriceResultLine[];
}

const LIST_QUERY_KEY = ["farfetch", "price-results"] as const;

function formatPeriod(report: PriceReport): string {
	if (!report.reportPeriodStart) return new Date(report.ingestedAt).toLocaleString();
	const start = new Date(report.reportPeriodStart).toLocaleDateString();
	if (!report.reportPeriodEnd) return start;
	const end = new Date(report.reportPeriodEnd).toLocaleDateString();
	return start === end ? start : `${start} - ${end}`;
}

/// Price results - Farfetch's daily verdict on the price proposals taken from
/// our MasterCatalogue upload. An ERROR line is a price update Farfetch never
/// applied, so it is still selling at the old price; a CONFLICT means it
/// already held that price (benign). Until 2026-09-03 nothing read these, and
/// a rejected price only surfaced when an order sold at the wrong one.
export function PriceResults({ initialReportId }: { initialReportId?: string }) {
	const qc = useQueryClient();
	const [expandedId, setExpandedId] = useState<string | null>(initialReportId ?? null);

	const query = useQuery({
		queryKey: LIST_QUERY_KEY,
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/farfetch/price-results");
			if (error) throw new Error("failed to load price results");
			return (data as { data: PriceReport[] }).data;
		},
	});

	const detailQuery = useQuery({
		queryKey: ["farfetch", "price-results", expandedId],
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/api/v1/farfetch/price-results/{id}", {
				params: { path: { id: expandedId ?? "" } },
			});
			if (error) throw new Error("failed to load report detail");
			return data as PriceReportDetail;
		},
		enabled: expandedId !== null,
	});

	// One poll walks BOTH result directories, so this shares the catalogue
	// endpoint rather than adding a second single-flight lock to race it.
	const pollMutation = useMutation({
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST(
				"/api/v1/farfetch/catalogue-results/poll-now" as never,
			);
			if (error) {
				const e = error as { detail?: string; error?: string; message?: string };
				const msg = e.detail ?? e.message ?? e.error ?? "";
				if (/already running/i.test(msg)) {
					throw new Error("a poll is already running");
				}
				throw new Error(msg || "Checking for results failed");
			}
			return data as { reportsIngested: number };
		},
		onSuccess: (result) => {
			qc.invalidateQueries({ queryKey: LIST_QUERY_KEY });
			qc.invalidateQueries({ queryKey: ["farfetch", "catalogue-results"] });
			toast.success(
				result.reportsIngested > 0
					? `Ingested ${result.reportsIngested} new report(s).`
					: "Checked - no new reports found.",
			);
		},
		onError: (e: Error) => toast.error(e.message),
	});

	function toggleExpand(id: string) {
		setExpandedId((prev) => (prev === id ? null : id));
	}

	if (query.isLoading) {
		return (
			<div className="space-y-2">
				{[0, 1, 2].map((i) => (
					<Skeleton key={i} className="h-9 w-full" />
				))}
			</div>
		);
	}

	if (query.isError) {
		return (
			<div
				role="alert"
				className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm space-y-2"
			>
				<p className="font-medium">Failed to load price results.</p>
				<Button type="button" variant="outline" size="sm" onClick={() => query.refetch()}>
					Retry
				</Button>
			</div>
		);
	}

	const reports = query.data ?? [];

	return (
		<div className="space-y-3">
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					Daily pricing reports from Farfetch. Rejected = the price update was NOT applied and
					Farfetch is still selling at the old price. Already held = no change needed.
				</p>
				<Button
					type="button"
					variant="outline"
					size="sm"
					disabled={pollMutation.isPending}
					onClick={() => pollMutation.mutate()}
				>
					{pollMutation.isPending && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
					Check price results now
				</Button>
			</div>

			{reports.length === 0 ? (
				<p className="text-sm text-muted-foreground">
					No price results yet - reports appear here after Farfetch processes the prices in an
					uploaded catalogue.
				</p>
			) : (
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Report</TableHead>
								<TableHead className="text-right w-20">Applied</TableHead>
								<TableHead className="text-right w-24">Already held</TableHead>
								<TableHead className="text-right w-20">Rejected</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{reports.map((r) => (
								<Fragment key={r.id}>
									<TableRow className="cursor-pointer" onClick={() => toggleExpand(r.id)}>
										<TableCell className="font-medium">
											<div>{r.filename}</div>
											<div className="text-xs text-muted-foreground">{formatPeriod(r)}</div>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant="secondary">{r.successCount}</Badge>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant="secondary">{r.conflictCount}</Badge>
										</TableCell>
										<TableCell className="text-right">
											<Badge variant={r.errorCount > 0 ? "destructive" : "secondary"}>
												{r.errorCount}
											</Badge>
										</TableCell>
									</TableRow>
									{expandedId === r.id && (
										<TableRow>
											<TableCell colSpan={4} className="bg-muted/30">
												{detailQuery.isLoading ? (
													<Skeleton className="h-9 w-full" />
												) : (detailQuery.data?.lines.length ?? 0) === 0 ? (
													<p className="text-sm text-muted-foreground py-1">
														Every price in this report was applied.
													</p>
												) : (
													<Table>
														<TableHeader>
															<TableRow>
																<TableHead>SKU</TableHead>
																<TableHead className="text-right">Price sent</TableHead>
																<TableHead>Result</TableHead>
																<TableHead>Description</TableHead>
															</TableRow>
														</TableHeader>
														<TableBody>
															{detailQuery.data?.lines.map((line) => (
																<TableRow
																	key={`${line.sku ?? ""}-${line.resultCode ?? ""}-${line.price ?? ""}`}
																>
																	<TableCell>{line.sku ?? "-"}</TableCell>
																	<TableCell className="text-right">
																		{line.price != null ? line.price.toFixed(2) : "-"}
																	</TableCell>
																	<TableCell>
																		<Badge
																			variant={
																				line.resultType === "ERROR" ? "destructive" : "outline"
																			}
																		>
																			{line.resultType === "ERROR" ? "Rejected" : "Already held"}
																		</Badge>
																	</TableCell>
																	<TableCell>{line.resultDesc ?? "-"}</TableCell>
																</TableRow>
															))}
														</TableBody>
													</Table>
												)}
											</TableCell>
										</TableRow>
									)}
								</Fragment>
							))}
						</TableBody>
					</Table>
				</div>
			)}
		</div>
	);
}
